// fogs.ts — pure rules for the full FOGS check in the app: the shared section keys, the
// return deep link, the "What we're working on together" rows, and when the Today card shows.
// No React, no network. Only section keys ever travel back from the check; never answers.
import type { MyProgressInstrument } from './sync';

export const FOGS_INSTRUMENT_ID = 'fogs-v1';

/** The five FOGS section keys, in display order (shared contract with the assessment page). */
export const FOGS_SECTION_KEYS = ['fear', 'obligation', 'guilt', 'shame', 'lossOfSelf'] as const;
export type FogsSectionKey = (typeof FOGS_SECTION_KEYS)[number];
export type ThemeKey = FogsSectionKey | 'ex';

/** The entry with the later `at` wins; a missing side loses; ties keep `a`. */
export function latestAt<T extends { at: string }>(a: T | null | undefined, b: T | null | undefined): T | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return Date.parse(b.at) > Date.parse(a.at) ? b : a;
}

export type FogsOpenMode = 'linked' | 'unlinked' | 'web' | 'failed';
/** What gets persisted: 'failed' never is. */
export type FogsStoredMode = Exclude<FogsOpenMode, 'failed'>;

export const THEME_LABELS: Record<ThemeKey, string> = {
  fear: 'Fear',
  obligation: 'Obligation',
  guilt: 'Guilt',
  shame: 'Shame',
  lossOfSelf: 'Loss of self',
  ex: 'Managing your ex (now)',
};

/** Web return: the live web app. The page appends done=1&flagged=... to this. Native has no
 *  return URL (the in-app browser's Done/X is used). */
export const WEB_RETURN_URL = 'https://app.renurture.org/?fogs-done=1';
export const FOGS_RETURN_MARKER = 'fogs-done';

/** Keep only valid, de-duplicated section keys, in canonical order. */
export function validFlagged(raw: Iterable<string> | null | undefined): FogsSectionKey[] {
  const set = new Set(raw ?? []);
  return FOGS_SECTION_KEYS.filter((k) => set.has(k));
}

/**
 * Parse the return from the assessment page: https://app.renurture.org/?fogs-done=1&done=1&flagged=a,b.
 * (A custom-scheme form org.renurture.app://fogs-done?done=1&flagged=... is also accepted, but the
 * site only returns by URL on web.) Returns null when the URL is not a
 * FOGS return or the check was not finished (done=1 is required). Unknown keys are dropped.
 */
export function parseFogsReturn(url: string | null | undefined): { flagged: FogsSectionKey[] } | null {
  if (!url) return null;
  let u: URL;
  try { u = new URL(url); } catch { return null; }
  const web = u.protocol === 'https:' || u.protocol === 'http:';
  const marked = web ? u.searchParams.has(FOGS_RETURN_MARKER) : u.hostname === FOGS_RETURN_MARKER || u.pathname.replace(/^\/+/, '') === FOGS_RETURN_MARKER;
  if (!marked) return null;
  if (u.searchParams.get('done') !== '1') return null;
  const flagged = (u.searchParams.get('flagged') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  return { flagged: validFlagged(flagged) };
}

/** The same URL with every FOGS return param removed (web: history.replaceState). */
export function stripFogsParams(url: string): string {
  const u = new URL(url);
  for (const k of [FOGS_RETURN_MARKER, 'done', 'flagged']) u.searchParams.delete(k);
  return u.pathname + (u.search || '') + u.hash;
}

/** Latest fogs-v1 wave from research-mine, if any (the secondary completion signal). */
export function fogsWaveFromMine(
  instruments: MyProgressInstrument[] | null | undefined,
): { flagged: FogsSectionKey[]; at: string | null } | null {
  const ins = instruments?.find((i) => i.id === FOGS_INSTRUMENT_ID);
  const waves = ins?.waves ?? [];
  if (!waves.length) return null;
  const latest = [...waves].sort((a, b) => String(a.at ?? '').localeCompare(String(b.at ?? ''))).pop()!;
  return { flagged: validFlagged(latest.flagged), at: latest.at ?? null };
}


// ── "What we're working on together" ─────────────────────────────────────────

export interface WorkingOnRow { key: ThemeKey; label: string }

export interface WorkingOn {
  rows: WorkingOnRow[];
  /** Where the five sections came from. null when there is nothing to show. */
  source: 'quick' | 'full' | null;
  /** ISO date of the full check, when source is 'full'. */
  at: string | null;
}

/**
 * Source precedence: the full check's flagged sections replace the quick check's for the five
 * sections (even when the full check flagged none); 'ex' always comes from the onboarding question.
 */
export function workingOn(
  needsThemes: { themes: string[]; at: string } | null | undefined,
  fogsResult: { flagged: string[]; at: string } | null | undefined,
): WorkingOn {
  const quick = new Set(needsThemes?.themes ?? []);
  const five = fogsResult ? new Set(validFlagged(fogsResult.flagged)) : quick;
  const rows: WorkingOnRow[] = [];
  for (const k of FOGS_SECTION_KEYS) if (five.has(k)) rows.push({ key: k, label: THEME_LABELS[k] });
  if (quick.has('ex')) rows.push({ key: 'ex', label: THEME_LABELS.ex });
  if (!rows.length) return { rows, source: null, at: fogsResult?.at ?? null };
  return { rows, source: fogsResult ? 'full' : 'quick', at: fogsResult?.at ?? null };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "5 Oct" style date, local time. */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? `${d.getDate()} ${MONTHS[d.getMonth()]}` : '';
}

export function sourceLine(w: WorkingOn): string {
  if (w.source === 'full') {
    const d = w.at ? shortDate(w.at) : '';
    return d ? `From your full check, ${d}` : 'From your full check';
  }
  return 'From your first questions';
}
